// src/services/paymentService.js
// D-289 — Payments as ledger events (Khaata model).
//
// WHY THIS EXISTS
// financial_transactions is an immutable ledger (SEC-01, 20 Sep 2026): a row
// is never edited after it is written. So a payment never changes an existing
// row — it is a NEW row with type "payment". Two kinds of payment exist,
// matching how money is actually paid on the ground:
//
//   1. PARTY payments — vendors and subcontractors are paid against their
//      running account (khaata), not bill by bill: "₹5 lakh due, paid ₹3
//      lakh, ₹2 lakh left". Partial payments, advances and one payment that
//      covers many bills all work without extra steps. A bill's own status
//      (Paid / Partly / Due) is CALCULATED from the khaata, oldest bill
//      settled first — it is never stored, so it can never go stale.
//
//   2. ITEM payments — one expense request or one transport trip, paid in
//      full. The source doc's status moves to "paid" in the SAME transaction
//      as the payment row, so the two can never disagree.
//
// This file is the ONLY code allowed to write a payment. No page writes
// payment data to Firestore directly (Constitution §10).
//
// FIELDS ON EVERY PAYMENT ROW
//   type: "payment", direction: "out"
//   amount        — cash that actually left the business (net of TDS).
//                   Cash / Bank Book uses this.
//   gross_amount  — how much of the bill / account this payment settles.
//                   Khaata balance uses this.
//   tds_amount    — TDS deducted, typed by the user (never auto-calculated;
//                   depositing TDS and filing returns stays in Tally / CA).
//   allocations   — OPTIONAL, party payments only: [{ bill_id, amount }] when
//                   the user chose which bills this payment is for. Those
//                   bills are settled first, the rest oldest-bill-first
//                   (src/utils/khaata.js).
// No project_id on purpose: the cost was already charged to the project when
// it was incurred. onFinancialTransactionCreate ignores type "payment", so a
// payment can never change project costing.

import { db } from "../firebase";
import {
  collection, doc, getDoc, setDoc, runTransaction, serverTimestamp,
} from "firebase/firestore";

export const PAYMENT_MODES = ["cash", "upi", "bank_transfer", "neft", "rtgs", "cheque"];

// Create a payment id when the Pay screen OPENS, and pass the same id on
// every "Confirm" click. Firestore Rules allow CREATE only on this ledger,
// so a second write with the same id counts as an update and is denied —
// a double click can never record the same payment twice.
export const newPaymentId = () => doc(collection(db, "financial_transactions")).id;

// Round to paise so 0.1 + 0.2 style float noise never reaches the ledger.
const toAmount = (v) => Math.round(Number(v || 0) * 100) / 100;

const PARTY_COLLECTION = { vendor: "vendors", subcontractor: "subcontractors" };

/* ==================================================
   1. PARTY PAYMENT — vendor or subcontractor khaata
================================================== */
export async function recordPartyPayment({
  payment_id,
  company_id,
  party_type,              // "vendor" | "subcontractor"
  party_id,
  party_name = "",
  gross_amount,            // how much of the account this settles
  tds_amount = 0,          // optional, typed by the user
  payment_mode,
  reference_no = "",       // UTR / cheque no.
  notes = "",
  paid_by,                 // caller's Auth uid
  allocations = [],        // optional [{ bill_id, amount }] — "Choose bills"
  paid_by_name = "",
}) {
  if (!payment_id) throw new Error("Payment id missing — please reopen the payment screen.");
  if (!company_id) throw new Error("Company not loaded.");
  if (!PARTY_COLLECTION[party_type]) throw new Error("Invalid party type.");
  if (!party_id) throw new Error("Select a vendor or subcontractor.");
  if (!paid_by) throw new Error("You are not signed in.");

  const gross = toAmount(gross_amount);
  const tds   = toAmount(tds_amount);
  if (!(gross > 0)) throw new Error("Amount must be greater than 0.");
  if (tds < 0 || tds >= gross) throw new Error("TDS must be less than the amount.");
  if (!PAYMENT_MODES.includes(payment_mode)) throw new Error("Select a payment mode.");

  const paymentRef = doc(db, "financial_transactions", payment_id);

  // Already recorded (e.g. a second click after a slow network) — report it,
  // never write twice.
  const existing = await getDoc(paymentRef);
  if (existing.exists()) return { payment_id, already_recorded: true };

  // The party must exist and belong to this company.
  const partySnap = await getDoc(doc(db, PARTY_COLLECTION[party_type], party_id));
  if (!partySnap.exists()) throw new Error("This vendor/subcontractor no longer exists.");
  if (partySnap.data().company_id !== company_id) throw new Error("Access denied.");

  // OPTIONAL "Choose bills" — the vendor/subcontractor named which bills this
  // payment is for. Stored on the immutable payment row; khaata.js settles
  // these bills first and the rest oldest-bill-first. Every chosen bill must
  // exist, belong to this company AND to this party, so a payment can never
  // be pointed at someone else's bill. Max 50 bills → at most ~100 reads.
  const cleanAllocations = [];
  const seenBills = new Set();
  for (const a of (Array.isArray(allocations) ? allocations : [])) {
    const bill_id = String(a?.bill_id || "");
    const amt     = toAmount(a?.amount);
    if (!bill_id || !(amt > 0)) continue;
    if (seenBills.has(bill_id)) throw new Error("The same bill is chosen twice.");
    seenBills.add(bill_id);
    cleanAllocations.push({ bill_id, amount: amt });
  }
  if (cleanAllocations.length > 50) throw new Error("Too many bills chosen in one payment (max 50).");
  const allocatedTotal = toAmount(cleanAllocations.reduce((s, a) => s + a.amount, 0));
  if (allocatedTotal > gross) throw new Error("Amounts for the chosen bills are more than the payment amount.");

  for (const a of cleanAllocations) {
    const billSnap = await getDoc(doc(db, "financial_transactions", a.bill_id));
    if (!billSnap.exists()) throw new Error("A chosen bill no longer exists. Please reopen the khaata.");
    const bill = billSnap.data();
    if (bill.company_id !== company_id) throw new Error("Access denied.");
    if (a.amount > toAmount(bill.amount)) throw new Error("Amount chosen for a bill is more than that bill.");

    let billPartyId = "";
    if (party_type === "vendor" && bill.type === "vendor") {
      billPartyId = bill.vendor_id || "";
    } else if (party_type === "subcontractor" && bill.type === "expense"
               && bill.reference_type === "subcontractor_work" && bill.reference_id) {
      // Subcontractor bill rows carry no party id — the work log does.
      // Only read when reference_id exists (reading a missing doc in a
      // sameCompany()-gated collection is denied — CORE_RULES §35).
      const logSnap = await getDoc(doc(db, "subcontractor_work_logs", bill.reference_id));
      billPartyId = logSnap.exists() ? (logSnap.data().subcontractor_id || "") : "";
    }
    if (billPartyId !== party_id) throw new Error("A chosen bill does not belong to this vendor/subcontractor.");
  }

  await setDoc(paymentRef, {
    company_id,
    type:          "payment",
    direction:     "out",
    party_type,
    party_id,
    party_name:    party_name || partySnap.data().vendor_name || partySnap.data().name || "",
    allocations:   cleanAllocations,
    gross_amount:  gross,
    tds_amount:    tds,
    amount:        toAmount(gross - tds),
    payment_mode,
    reference_no:  String(reference_no || "").trim(),
    notes:         String(notes || "").trim(),
    paid_by,
    paid_by_name,
    created_at:    serverTimestamp(),
  });

  return { payment_id, already_recorded: false };
}

/* ==================================================
   1b. PAYMENT REVERSAL (D-315) — cancels ONE vendor / subcontractor payment
   A payment row is never edited or deleted (immutable ledger). A wrong
   payment is cancelled by a NEW row: type "payment_reversal", doc id
   "prev_" + the payment id — so a payment can be reversed only once (the
   ledger is create-only; a second write is refused). Amount, gross, TDS,
   party and mode are copied EXACTLY from the original (Firestore Rules
   compare them). owner / accounts only, reason required. khaata.js then
   drops the payment (its bills go back to Due) and cashBook.js shows the
   reversal as a minus Out in the same mode. Expense / transport item
   payments are not reversible yet (they also changed their source doc).
================================================== */
export async function reversePartyPayment({
  company_id,
  payment_id,
  reason = "",
  created_by,              // caller's Auth uid
  created_by_name = "",
}) {
  if (!company_id) throw new Error("Company not loaded.");
  if (!payment_id) throw new Error("Payment missing.");
  if (!created_by) throw new Error("You are not signed in.");
  const why = String(reason || "").trim();
  if (!why) throw new Error("Enter a reason for reversing this payment.");
  if (why.length > 500) throw new Error("Reason is too long (max 500 characters).");

  const payRef = doc(db, "financial_transactions", payment_id);
  const revRef = doc(db, "financial_transactions", `prev_${payment_id}`);

  return runTransaction(db, async (tx) => {
    const revSnap = await tx.get(revRef);
    if (revSnap.exists()) return { reversal_id: revRef.id, already_reversed: true };

    const paySnap = await tx.get(payRef);
    if (!paySnap.exists()) throw new Error("Payment not found.");
    const p = paySnap.data();
    if (p.company_id !== company_id) throw new Error("Access denied.");
    if (p.type !== "payment") throw new Error("Only a payment can be reversed here.");
    if (!PARTY_COLLECTION[p.party_type]) {
      throw new Error("Only a vendor / subcontractor payment can be reversed here.");
    }

    tx.set(revRef, {
      company_id,
      type:            "payment_reversal",
      direction:       "in",
      reverses_id:     payment_id,
      party_type:      p.party_type,
      party_id:        p.party_id,
      party_name:      p.party_name || "",
      amount:          p.amount,
      gross_amount:    p.gross_amount,
      tds_amount:      p.tds_amount ?? 0,
      payment_mode:    p.payment_mode,
      reference_no:    p.reference_no || "",
      reason:          why,
      created_by,
      created_by_name,
      created_at:      serverTimestamp(),
    });

    return { reversal_id: revRef.id, already_reversed: false };
  });
}

/* ==================================================
   2. ITEM PAYMENT — one expense request or one transport trip, in full
================================================== */
const ITEM_RULES = {
  // Expense request: approved by SM/Manager, now waiting for money.
  site_purchase_request: {
    collection: "site_purchase_requests",
    isPayable:  (d) => d.type === "expense" && d.status === "approved",
    amountOf:   (d) => d.amount,
    categoryOf: (d) => d.category || "expense",
    payeeOf:    (d) => d.requested_by_name || "",
    refOf:      (d) => d.description || "",
  },
  // Transport trip: logged by Store/Manager, goes straight to Accounts.
  // Paid per TRIP (the driver is paid once), not per stop — the per-stop
  // ledger rows exist only to split the cost across projects.
  transport_log: {
    collection: "transport_logs",
    isPayable:  (d) => d.status === "payment_pending",
    amountOf:   (d) => d.total_cost,
    categoryOf: () => "transport",
    payeeOf:    (d) => d.driver_name || "",
    refOf:      (d) => `Trip ${d.trip_date || ""}`.trim(),
  },
};

export async function recordItemPayment({
  payment_id,
  company_id,
  item_type,               // "site_purchase_request" | "transport_log"
  item_id,
  payment_mode,
  reference_no = "",
  notes = "",
  paid_by,
  paid_by_name = "",
}) {
  if (!payment_id) throw new Error("Payment id missing — please reopen the payment screen.");
  if (!company_id) throw new Error("Company not loaded.");
  const rule = ITEM_RULES[item_type];
  if (!rule) throw new Error("Invalid item type.");
  if (!item_id) throw new Error("Item missing.");
  if (!paid_by) throw new Error("You are not signed in.");
  if (!PAYMENT_MODES.includes(payment_mode)) throw new Error("Select a payment mode.");

  const paymentRef = doc(db, "financial_transactions", payment_id);
  const itemRef    = doc(db, rule.collection, item_id);

  // One transaction: re-reads the item, so two people paying the same item
  // at the same moment cannot both succeed.
  return runTransaction(db, async (tx) => {
    const paymentSnap = await tx.get(paymentRef);
    if (paymentSnap.exists()) return { payment_id, already_recorded: true };

    const itemSnap = await tx.get(itemRef);
    if (!itemSnap.exists()) throw new Error("Item not found.");
    const item = itemSnap.data();
    if (item.company_id !== company_id) throw new Error("Access denied.");
    if (!rule.isPayable(item)) throw new Error("This item is not waiting for payment (already paid or not approved).");

    const amount = toAmount(rule.amountOf(item));
    if (!(amount > 0)) throw new Error("This item has no amount to pay.");

    tx.set(paymentRef, {
      company_id,
      type:          "payment",
      direction:     "out",
      payable_type:  item_type,
      payable_id:    item_id,
      category:      rule.categoryOf(item),
      party_name:    rule.payeeOf(item),
      gross_amount:  amount,
      tds_amount:    0,
      amount,
      payment_mode,
      reference_no:  String(reference_no || "").trim(), description: rule.refOf(item),
      notes:         String(notes || "").trim(),
      paid_by,
      paid_by_name,
      created_at:    serverTimestamp(),
    });

    tx.update(itemRef, {
      status:        "paid",
      payment_id,
      payment_mode,
      paid_by,
      paid_by_name,
      paid_at:       serverTimestamp(),
      updated_at:    serverTimestamp(),
    });

    return { payment_id, already_recorded: false };
  });
}